import { useState } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", trade: "", invite: false };

// Creates a supplier record (+ optional portal invite), then hands it back via onCreated.
export default function NewSupplierDialog({ open, onClose, onCreated, testIdPrefix = "new-supplier", description, actionWord = "Assign" }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    if (form.invite && !form.email.trim()) { toast.error("An email address is required to send a portal invite"); return; }
    setSaving(true);
    try {
      const { data: created } = await API.post("/suppliers", {
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        trade: form.trade.trim(),
      });
      let invited = false;
      if (form.invite) {
        try {
          await API.post(`/suppliers/${created.id}/invite`);
          invited = true;
        } catch {
          toast.warning("Supplier added, but the portal invite failed — resend it from the Suppliers page.");
        }
      }
      setForm(EMPTY);
      onClose();
      await onCreated?.(created, invited);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add supplier"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setForm(EMPTY); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Add New Supplier</DialogTitle>
          <DialogDescription>{description || "Enter the details of a supplier who isn't on the system yet. They'll be added to your supplier list and assigned."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Company Name *</Label>
            <Input value={form.company_name} onChange={e => set({ company_name: e.target.value })} placeholder="e.g. AG Plumbing Ltd" className="mt-1" data-testid={`${testIdPrefix}-company`} />
          </div>
          <div>
            <Label className="text-xs">Contact Name</Label>
            <Input value={form.contact_name} onChange={e => set({ contact_name: e.target.value })} placeholder="e.g. Alan Smith" className="mt-1" data-testid={`${testIdPrefix}-contact`} />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={e => set({ email: e.target.value })} placeholder="supplier@company.com" className="mt-1" data-testid={`${testIdPrefix}-email`} />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={form.phone} onChange={e => set({ phone: e.target.value })} placeholder="07..." className="mt-1" data-testid={`${testIdPrefix}-phone`} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Trade</Label>
            <Input value={form.trade} onChange={e => set({ trade: e.target.value })} placeholder="e.g. Plumbing, Electrical, Roofing" className="mt-1" data-testid={`${testIdPrefix}-trade`} />
          </div>
        </div>
        <label className="flex items-start gap-2 cursor-pointer border border-border rounded-md p-2.5 bg-muted/20 hover:bg-muted/40">
          <input
            type="checkbox"
            checked={form.invite}
            onChange={e => set({ invite: e.target.checked })}
            className="mt-0.5 h-4 w-4 accent-primary"
            data-testid={`${testIdPrefix}-invite-checkbox`}
          />
          <span className="text-xs">
            <span className="font-semibold">Onboard them into the portal</span>
            <span className="block text-muted-foreground mt-0.5">Emails them an invite to set a password so they can view jobs, works orders and submit updates. Requires an email address.</span>
          </span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.company_name.trim()} data-testid={`${testIdPrefix}-confirm`}>
            {saving ? "Adding..." : form.invite ? `Add, ${actionWord} & Invite` : `Add & ${actionWord}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
